import { useTrackActions } from "@/utils/hooks/useTracking";
import {
  AnalyticsTrackingEvent,
  MOBILE_SCREEN_SIZE,
} from "@brandclub/common-ui";
import { styled, Checkbox, CheckboxProps, useTheme, Box } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

import { ShippingAddress } from "@/components/Checkout/types";
import StoreAddressAutocompleteInput, {
  AutocompleteAddress,
} from "../CheckoutAddressAutocompleteInput";
import { CheckoutInput } from "@/components/Checkout/components/CheckoutInput";
import { useLocation, useSearchParams } from "react-router-dom";

const INPUT_WIDTH = 240;
const MOBILE_INPUT_WIDTH = 120;

const InputContainerContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "0.75rem",
  flexDirection: "row",
  justifyContent: "space-between",
}));
export const InputContainer = (props: React.ComponentProps<typeof Box>) => (
  <InputContainerContainer
    display="flex"
    flexDirection="row"
    justifyContent="space-between"
    {...props}
  />
);

const UnCheckedIcon = styled("span")(({ theme }) => ({
  width: 24,
  height: 24,
  backgroundColor: "#fff",
  border: "1px solid #dcdddc",
  borderRadius: "4px",
  margin: "0px",
  padding: "0px",
  boxSizing: "border-box",
}));

const CheckedIcon = () => {
  return (
    <CheckRoundedIcon
      sx={(theme) => ({
        width: 24,
        height: 24,
        margin: "0px",
        padding: "0px",
        color: "#fff",
        backgroundColor: theme.vars.palette.text.primary,
        borderRadius: "4px",
        boxSizing: "border-box",
      })}
    />
  );
};

// Inspired by blueprintjs
const BpCheckbox = (props: CheckboxProps) => {
  return (
    <Checkbox
      sx={{
        "&:hover": { bgcolor: "transparent" },
        padding: "0px",
        paddingRight: "7px",
      }}
      disableRipple
      color="default"
      icon={<UnCheckedIcon />}
      checkedIcon={<CheckedIcon />}
      {...props}
    />
  );
};

export interface BillingAddressCheckboxProps {
  useShippingAddress: boolean;
  onUseShippingAddressChange: (checked: boolean) => void;
  disabled?: boolean;
}

export const BillingAddressCheckbox = ({
  useShippingAddress,
  onUseShippingAddressChange,
  disabled = false,
}: BillingAddressCheckboxProps) => {
  const theme = useTheme();
  const handleUseShippingAddressChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onUseShippingAddressChange(event.target.checked);
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
      <BpCheckbox
        checked={useShippingAddress}
        onChange={handleUseShippingAddressChange}
        disabled={disabled}
        disableRipple
        disableFocusRipple
        disableTouchRipple
      />
      <span style={{ color: theme.vars.palette.text.primary, fontSize: 14 }}>
        My billing and shipping address are the same
      </span>
    </div>
  );
};

const Container = styled("div")(({ theme }) => ({
  transition: "all 0.3s ease-in-out",

  display: "flex",
  flexDirection: "column",
  gap: "0.75rem",
  ".title": {
    fontSize: 20,
    fontWeight: 600,

    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      fontSize: 14,
    },
  },
}));

interface AddressFormProps {
  value: Partial<ShippingAddress>;
  onChange: (address: Partial<ShippingAddress>) => void;
  disabled?: boolean;
}

const getOrderIdFromPathname = (pathname: string): string | null => {
  const match = pathname.match(/\/purchase\/([^/?#]+)/);
  return match ? match[1] : null;
};

const AddressForm = ({
  value,
  onChange,
  disabled = false,
}: AddressFormProps) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [trackAction] = useTrackActions();

  const orderId =
    searchParams.get("orderId") ?? getOrderIdFromPathname(location.pathname);
  const checkoutIntentId = searchParams.get("checkoutIntentId");

  const isPostSubmission = !!orderId;

  const handleChange = (field: keyof ShippingAddress, newValue: string) => {
    // Track key form field interactions
    if (
      checkoutIntentId &&
      [
        "address1",
        "city",
        "state",
        "zipCode",
        "firstName",
        "lastName",
      ].includes(field)
    ) {
      trackAction(AnalyticsTrackingEvent.CHECKOUT_STEP_VIEWED, {
        checkoutIntentId: checkoutIntentId,
        stepName: "shipping",
        viewType: "addressField",
        fieldName: field,

        checkoutPhase: isPostSubmission ? "post_submission" : "pre_submission",
        ...(orderId && { orderId }),
      });
    }

    onChange({
      ...value,
      [field]: newValue,
    });
  };

  return (
    <Container>
      <InputContainer>
        <CheckoutInput
          label={"First Name"}
          value={value.firstName || ""}
          onChange={(e) => handleChange("firstName", e.target.value)}
          disabled={disabled}
          sx={(theme) => ({
            minWidth: INPUT_WIDTH,
            [theme.breakpoints.down("md")]: {
              minWidth: MOBILE_INPUT_WIDTH,
            },
          })}
        />
        <CheckoutInput
          label={"Last Name"}
          value={value.lastName || ""}
          onChange={(e) => handleChange("lastName", e.target.value)}
          disabled={disabled}
          sx={(theme) => ({
            minWidth: INPUT_WIDTH,
            [theme.breakpoints.down("md")]: {
              minWidth: MOBILE_INPUT_WIDTH,
            },
          })}
        />
      </InputContainer>
      <InputContainer>
        <StoreAddressAutocompleteInput
          searchTerm={value.address1 || ""}
          numSuggestions={4}
          selectAddressCallback={(address: AutocompleteAddress) => {
            // Track autocomplete address selection
            if (checkoutIntentId) {
              trackAction(AnalyticsTrackingEvent.CHECKOUT_STEP_VIEWED, {
                checkoutIntentId: checkoutIntentId,
                stepName: "shipping",
                viewType: "addressAutocomplete",
                checkoutPhase: isPostSubmission
                  ? "post_submission"
                  : "pre_submission",
                ...(orderId && { orderId }),
              });
            }

            onChange({
              ...value,
              address1: address?.line1 || "",
              city: address?.city || "",
              state: address?.state || "",
              zipCode: address?.zip || "",
            });
          }}
          renderInput={(param: any) => (
            <CheckoutInput
              placeholder="Begin typing to search"
              label={"Street address"}
              value={value.address1}
              onChange={(e) => {
                return handleChange("address1", e.target.value);
              }}
              disabled={disabled}
              {...param}
              InputProps={{
                ...param.InputProps,
              }}
              inputProps={{
                ...param.inputProps,
                value: value.address1,
              }}
            />
          )}
        />
      </InputContainer>

      <CheckoutInput
        label={"Suite, Apartment, C/O"}
        value={value.address2 || ""}
        disabled={disabled}
        onChange={(e) => handleChange("address2", e.target.value)}
        sx={(theme) => ({
          minWidth: INPUT_WIDTH,
          [theme.breakpoints.down("md")]: {
            minWidth: MOBILE_INPUT_WIDTH,
          },
        })}
      />
      <InputContainer>
        <CheckoutInput
          label={"City"}
          value={value.city || ""}
          disabled={disabled}
          onChange={(e) => handleChange("city", e.target.value)}
          sx={(theme) => ({
            minWidth: INPUT_WIDTH,
            [theme.breakpoints.down("md")]: {
              minWidth: MOBILE_INPUT_WIDTH,
            },
          })}
        />

        <CheckoutInput
          label={"State"}
          value={value.state || ""}
          disabled={disabled}
          onChange={(e) => handleChange("state", e.target.value)}
          sx={(theme) => ({
            minWidth: INPUT_WIDTH,
            [theme.breakpoints.down("md")]: {
              minWidth: MOBILE_INPUT_WIDTH,
            },
          })}
        />
      </InputContainer>
      <CheckoutInput
        label={"Zip Code"}
        value={value.zipCode || ""}
        disabled={disabled}
        onChange={(e) => handleChange("zipCode", e.target.value)}
        sx={(theme) => ({
          minWidth: INPUT_WIDTH,
          [theme.breakpoints.down("md")]: {
            minWidth: MOBILE_INPUT_WIDTH,
          },
        })}
      />
    </Container>
  );
};

export default AddressForm;
